<script setup lang="ts">
import { usePhoneFormatter } from '@/composables/formatting/usePhoneFormatter'

const { t } = useI18n()
const { formatPhone } = usePhoneFormatter()

const open = defineModel<boolean>('open', { default: false })

const props = withDefaults(
  defineProps<{
    configName?: string
    phones?: string | null
  }>(),
  {
    configName: '',
    phones: null,
  },
)

const emit = defineEmits<(e: 'unassign', phones: string[]) => void>()

const phoneList = computed(() => {
  if (!props.phones) return []
  return props.phones.split(', ')
})

const hasMultiplePhones = computed(() => phoneList.value.length > 1)

const selectedPhone = ref<string>('all')

const phoneOptions = computed(() => {
  const options = phoneList.value.map((phone) => ({
    value: phone,
    label: formatPhone(phone),
  }))
  if (hasMultiplePhones.value) {
    options.unshift({ value: 'all', label: t('all') })
  }
  return options
})

watch(open, (isOpen) => {
  if (isOpen) {
    selectedPhone.value = hasMultiplePhones.value ? 'all' : phoneList.value[0] || 'all'
  }
})

const closeModal = () => {
  open.value = false
  return true
}

const onUnassign = () => {
  const phonesToUnassign = selectedPhone.value === 'all' ? phoneList.value : [selectedPhone.value]
  emit('unassign', phonesToUnassign)
  return true
}
</script>

<template>
  <OModal
    v-model:open="open"
    :secondary-action-title="t('cancel')"
    :primary-action-title="t('unassign')"
    primary-action-enabled
    secondary-action-enabled
    :secondary-action-promise="closeModal"
    :primary-action-promise="onUnassign"
    danger
    :title="t('unassign_phone_number')"
  >
    <template #content>
      <div class="flex flex-col gap-4 pb-1">
        <i18n-t
          keypath="unassign_phone_number_subtitle"
          tag="span"
          class="typography-body-2 text-neutral-1"
        >
          <span class="typography-body-2-strong">{{ configName }}</span>
        </i18n-t>

        <ODropdown
          v-if="hasMultiplePhones"
          v-model="selectedPhone"
          :options="phoneOptions"
          medium
          teleport
        />
      </div>
    </template>
  </OModal>
</template>
